if (!customElements.get("added-to-cart-popup")) {
  customElements.define(
    "added-to-cart-popup",
    class AddedToCartPopup extends HTMLElement {
      constructor() {
        super();

        this.panel = this.querySelector("#AddedToCartPopup");
        this.productSlot = this.querySelector("#AddedToCartPopup-Product");
        this.recsRegion = this.querySelector("#AddedToCartPopup-Recs");
        this.overlay = this.querySelector("[data-atc-overlay]");

        this.recUrl = this.dataset.recommendationsUrl || "";
        this.recSection =
          this.dataset.recommendationsSection ||
          "added-to-cart-recommendations";

        // Slide-out duration; keep in sync with the CSS transition.
        this.transitionMs = 320;
        // Safety net only: if the recommendations request stalls this long,
        // the drawer opens anyway with the confirmation (never block feedback).
        this.openFallbackDelay = 2500;

        this.addId = 0;
        this.abortedId = null;
        this.openTimer = null;
        this._autoCloseTimer = null;

        this.close = this.close.bind(this);
        this.onKeydown = this.onKeydown.bind(this);

        this.querySelectorAll("[data-atc-popup-close]").forEach((el) =>
          el.addEventListener("click", this.close),
        );

        const viewCartButton = this.querySelector("[data-atc-popup-view-cart]");
        if (viewCartButton) {
          viewCartButton.addEventListener("click", () => this.openCart());
        }

        if (this.overlay) this.overlay.addEventListener("click", this.close);
      }

      /* ----------------------------------------------------------------
         Interface used by product-form.js / card-product-add-to-cart.js
      ----------------------------------------------------------------- */

      // Only the tiny cart-icon-bubble section is requested on add-to-cart.
      // Asking for "cart-drawer" here makes Shopify render the whole mini
      // cart (loyalty, private sales, cross-sell, gift products, recently
      // viewed) inside every add request, which is what makes adds feel slow.
      // The drawer is refreshed lazily instead — see updateCartDrawer().
      getSectionsToRender() {
        return [{ id: "cart-icon-bubble" }];
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      showOptimistic(product) {
        if (!product || (!product.title && !product.imageUrl)) return null;

        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && cartDrawer.classList.contains("active")) return null;

        this.addId += 1;
        const myId = this.addId;

        this.buildProductSlot({
          imageUrl: product.imageUrl,
          title: product.title,
          variantText: product.variantText,
        });

        if (this.classList.contains("is-active")) {
          this.bump();
          this.loadRecommendations(product.productId, myId);
        } else {
          this.prepareAndOpen(product.productId, myId);
        }

        return myId;
      }

      cancelOptimistic(addId) {
        if (addId == null || addId !== this.addId) return;
        this.abortedId = addId;
        clearTimeout(this.openTimer);
        if (this.classList.contains("is-active")) this.close();
      }

      renderContents(response) {
        this.updateCartIcon(response);
        this.updateCartDrawer(response);
      }

      updateCartDrawer(response) {
        const cartDrawer = document.querySelector("cart-drawer");
        if (!cartDrawer) return;

        const sectionHTML = response?.sections?.["cart-drawer"];

        // Drawer already open (e.g. adding from the recommendations inside
        // it) — it has to update in place right now.
        if (cartDrawer.classList.contains("active")) {
          if (sectionHTML && typeof cartDrawer.renderContents === "function") {
            cartDrawer.renderContents(response);
          } else if (typeof cartDrawer.refresh === "function") {
            cartDrawer.refresh();
          }
          return;
        }

        // Drawer closed: don't pay for the render during the add. Mark it
        // stale — cart-drawer.js warms it in the background, and open() waits
        // on that refresh if the shopper gets there first.
        if (typeof cartDrawer.markStale === "function") {
          cartDrawer.markStale();
        }
      }
      /* ----------------------------------------------------------------
         Recommendations + open sequencing
      ----------------------------------------------------------------- */

      prepareAndOpen(productId, myId) {
        if (this.recsRegion) this.recsRegion.innerHTML = "";

        let opened = false;
        const openNow = () => {
          if (opened) return;
          if (this.abortedId === myId) return;
          if (myId !== this.addId) return;
          opened = true;
          clearTimeout(this.openTimer);
          this.open();
        };

        clearTimeout(this.openTimer);
        this.openTimer = setTimeout(openNow, this.openFallbackDelay);

        this.loadRecommendations(productId, myId, openNow);
      }

      loadRecommendations(productId, myId, onDone) {
        const finish = () => {
          if (typeof onDone === "function") onDone();
        };

        if (!productId || !this.recUrl || !this.recsRegion) {
          finish();
          return;
        }

        const url = `${this.recUrl}?section_id=${encodeURIComponent(
          this.recSection,
        )}&product_id=${encodeURIComponent(productId)}&limit=10`;

        fetch(url)
          .then((response) => response.text())
          .then((html) => {
            if (myId !== this.addId) return;

            const recs = new DOMParser()
              .parseFromString(html, "text/html")
              .querySelector("[data-atc-recs]");

            this.recsRegion.innerHTML = recs ? recs.innerHTML : "";

            if (this.recsRegion.querySelector(".card-wrapper")) {
              document.dispatchEvent(new CustomEvent("card-product-loaded"));
            }
          })
          .catch(() => {
            /* recommendations are non-critical — drawer still opens */
          })
          .finally(finish);
      }

      /* ----------------------------------------------------------------
         Open / close
      ----------------------------------------------------------------- */

      open() {
        clearTimeout(this.openTimer);
        this.classList.add("is-active");
        document.body.classList.add("overflow-hidden");
        document.addEventListener("keydown", this.onKeydown);

        if (typeof trapFocus === "function") {
          this.preserveScroll(() => trapFocus(this.panel || this));
        }

        // Auto-close after 3s if no interaction.
        this.startAutoClose();
      }

      startAutoClose() {
        this.clearAutoClose();
        this._autoCloseTimer = setTimeout(() => this.close(), 3000);

        // Cancel if the user interacts with the drawer.
        this.panel?.addEventListener("mouseenter", () => this.clearAutoClose(), { once: true });
        this.panel?.addEventListener("touchstart", () => this.clearAutoClose(), { once: true });
      }

      clearAutoClose() {
        if (this._autoCloseTimer) {
          clearTimeout(this._autoCloseTimer);
          this._autoCloseTimer = null;
        }
      }

      preserveScroll(fn) {
        const html = document.documentElement;
        const previous = html.style.scrollBehavior;
        const x = window.scrollX;
        const y = window.scrollY;
        html.style.scrollBehavior = "auto";
        fn();
        window.scrollTo(x, y);
        html.style.scrollBehavior = previous;
      }

      close() {
        this.clearAutoClose();
        if (!this.classList.contains("is-active")) {
          clearTimeout(this.openTimer);
          return;
        }
        this.classList.remove("is-active");
        clearTimeout(this.openTimer);
        document.body.classList.remove("overflow-hidden");
        document.removeEventListener("keydown", this.onKeydown);

        if (typeof removeTrapFocus === "function") {
          this.preserveScroll(() => removeTrapFocus(this.activeElement));
        }
      }

      openCart() {
        const cartDrawer = document.querySelector("cart-drawer");
        const cartIcon = document.getElementById("cart-icon-bubble");
        this.close();

        if (cartDrawer && typeof cartDrawer.open === "function") {
          setTimeout(
            () => cartDrawer.open(cartIcon || undefined),
            this.transitionMs,
          );
        } else if (window.routes && window.routes.cart_url) {
          window.location = window.routes.cart_url;
        }
      }

      onKeydown(event) {
        if (event.code === "Escape") this.close();
      }

      /* ----------------------------------------------------------------
         Rendering helpers
      ----------------------------------------------------------------- */

      buildProductSlot({ imageUrl, title, variantText }) {
        if (!this.productSlot) return;
        this.productSlot.textContent = "";

        if (imageUrl) {
          const imageWrap = document.createElement("div");
          imageWrap.className = "atc-drawer__image";
          const img = document.createElement("img");
          img.src = imageUrl;
          img.alt = title || "";
          img.loading = "eager";
          img.fetchPriority = "high";
          img.width = 56;
          img.height = 56;
          imageWrap.appendChild(img);
          this.productSlot.appendChild(imageWrap);
        }

        const details = document.createElement("div");
        details.className = "atc-drawer__details";

        const titleEl = document.createElement("p");
        titleEl.className = "atc-drawer__title";
        titleEl.textContent = title || "";
        details.appendChild(titleEl);

        if (variantText) {
          const variantEl = document.createElement("span");
          variantEl.className = "atc-drawer__variant";
          variantEl.textContent = variantText;
          details.appendChild(variantEl);
        }

        this.productSlot.appendChild(details);
      }

      bump() {
        if (!this.panel) return;
        this.panel.classList.remove("atc-drawer--bump");
        void this.panel.offsetWidth;
        this.panel.classList.add("atc-drawer--bump");
      }

      updateCartIcon(response) {
        const sectionHTML = response?.sections?.["cart-icon-bubble"];

        // If the section is missing from the response, don't silently leave a
        // stale count in the header — read the live cart instead.
        if (!sectionHTML) {
          this.refreshCartCount();
          return;
        }

        const cartIcon = document.getElementById("cart-icon-bubble");
        if (!cartIcon) return;

        cartIcon.innerHTML = this.getSectionInnerHTML(
          sectionHTML,
          ".shopify-section",
        );
      }

      // Fallback badge update straight from /cart.js. Mirrors the markup in
      // sections/cart-icon-bubble.liquid (a .text-2xs span, only when count > 0).
      refreshCartCount() {
        const cartUrl = (window.routes && window.routes.cart_url) || "/cart";

        fetch(`${cartUrl}.js`)
          .then((r) => r.json())
          .then((cart) => {
            const cartIcon = document.getElementById("cart-icon-bubble");
            if (!cartIcon) return;

            let badge = cartIcon.querySelector(".text-2xs");

            if (cart.item_count > 0) {
              if (!badge) {
                badge = document.createElement("span");
                badge.className = "text-2xs";
                cartIcon.appendChild(badge);
              }
              badge.textContent = cart.item_count;
            } else if (badge) {
              badge.remove();
            }
          })
          .catch(() => {
            /* badge is cosmetic — never block on this */
          });
      }

      getSectionInnerHTML(html, selector) {
        const element = new DOMParser()
          .parseFromString(html, "text/html")
          .querySelector(selector);
        return element ? element.innerHTML : html;
      }
    },
  );
}